"use client"

import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

export type SourceGalleryImage = {
  src: string
  alt: string
}

export function SourceGalleryGrid({ images }: { images: SourceGalleryImage[] }) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const selectedImage = selectedIndex === null ? undefined : images[selectedIndex]

  const closeLightbox = () => {
    setSelectedIndex(null)
    window.requestAnimationFrame(() => triggerRef.current?.focus())
  }

  const moveSelection = (direction: 1 | -1) => {
    setSelectedIndex((current) => {
      if (current === null || images.length < 2) return current
      return (current + direction + images.length) % images.length
    })
  }

  useEffect(() => {
    if (selectedIndex === null) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    closeButtonRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        closeLightbox()
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        moveSelection(-1)
      }
      if (event.key === "ArrowRight") {
        event.preventDefault()
        moveSelection(1)
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [selectedIndex])

  return (
    <>
      <div className="source-gallery-grid-prose source-prose" aria-label="Gallery images">
        {images.map((image, index) => (
          <button
            key={`${image.src}-${index}`}
            type="button"
            className="source-gallery-item"
            aria-label={`Open ${image.alt || `gallery image ${index + 1}`}`}
            onClick={(event) => {
              triggerRef.current = event.currentTarget
              setSelectedIndex(index)
            }}
          >
            <img src={image.src} alt={image.alt} loading="lazy" decoding="async" referrerPolicy="no-referrer" />
          </button>
        ))}
      </div>

      {selectedImage && selectedIndex !== null ? (
        <div
          className="source-gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-labelledby="source-gallery-lightbox-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeLightbox()
          }}
        >
          <div className="source-gallery-lightbox-panel">
            <button
              ref={closeButtonRef}
              type="button"
              className="source-gallery-lightbox-close"
              aria-label="Close image viewer"
              onClick={closeLightbox}
            >
              <X aria-hidden="true" size={20} strokeWidth={1.8} />
            </button>
            <button
              type="button"
              className="source-gallery-lightbox-nav source-gallery-lightbox-prev"
              aria-label="Previous gallery image"
              onClick={() => moveSelection(-1)}
            >
              <ChevronLeft aria-hidden="true" size={24} strokeWidth={1.8} />
            </button>
            <div className="source-gallery-lightbox-frame">
              <img
                className="source-gallery-lightbox-image"
                src={selectedImage.src}
                alt={selectedImage.alt}
                decoding="async"
                referrerPolicy="no-referrer"
              />
            </div>
            <button
              type="button"
              className="source-gallery-lightbox-nav source-gallery-lightbox-next"
              aria-label="Next gallery image"
              onClick={() => moveSelection(1)}
            >
              <ChevronRight aria-hidden="true" size={24} strokeWidth={1.8} />
            </button>
            <div className="source-gallery-lightbox-caption">
              <p id="source-gallery-lightbox-title">{selectedImage.alt || `Gallery image ${selectedIndex + 1}`}</p>
              <span>{selectedIndex + 1} of {images.length}</span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
